import { Logger } from '../../common/types/loggerTypes';
import { clientStorageDrivers, BellaClientStorageDriver, StorageRecord } from './types';
import getDriver from './drivers';
import {
    createRecord,
    getExpirationForDateTTL,
    getExpirableItemKey,
    getExpirableItemExpirationKey,
} from './recordManager';

export const defaultDriverKey = clientStorageDrivers.localStorage;

class ClientStorage {
    private driver: BellaClientStorageDriver;

    private driverKey: clientStorageDrivers;

    private logger: Logger;

    public constructor(logger: Logger, driverKey?: clientStorageDrivers) {
        this.logger = logger;
        this.driverKey = driverKey || defaultDriverKey;
        this.driver = getDriver(this.driverKey);
    }

    public getItem(key: string) {
        const itemString = this.driver.getItem(key) || this.getExpirableItem(key);

        if (!itemString) {
            return itemString;
        }

        try {
            return JSON.parse(itemString) as StorageRecord;
        } catch (error) {
            this.logger.error(
                `Failed to parse client storage item for driver: ${this.driverKey}, key: ${key} with value: ${itemString}`
            );

            return itemString;
        }
    }

    public setItem(key: string, value: StorageRecord, ttl?: number) {
        if (ttl !== undefined) {
            this.setExpirableItem(key, createRecord(value), ttl);
        } else {
            this.driver.setItem(key, createRecord(value));
        }
    }

    private getExpirableItem(key: string) {
        const item = this.driver.getItem(getExpirableItemKey(key));

        if (item) {
            this.checkRecordExpiration(key);
        }

        return item;
    }

    private setExpirableItem(key: string, record: string, ttl: number) {
        this.driver.setItem(getExpirableItemKey(key), record);
        this.driver.setItem(getExpirableItemExpirationKey(key), getExpirationForDateTTL(ttl));
    }

    public removeItem(key: string) {
        this.driver.removeItem(key);

        if (this.driver.getItem(getExpirableItemKey(key))) {
            this.driver.removeItem(getExpirableItemKey(key));
            this.driver.removeItem(getExpirableItemExpirationKey(key));
        }
    }

    private checkRecordExpiration(key: string) {
        const itemExpirationTime = this.driver.getItem(getExpirableItemExpirationKey(key)) || '';

        if (itemExpirationTime && new Date(itemExpirationTime) < new Date()) {
            this.removeItem(getExpirableItemKey(key));
            this.removeItem(getExpirableItemExpirationKey(key));
        }
    }
}

export default ClientStorage;
